import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AntiPunctureTires = () => {
  const [activeTab, setActiveTab] = useState('contact'); // Default to public contact tab
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    fleetSize: '',
    vehicleTypes: '',
    description: '',
    timeline: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Check if user is admin (you can replace this with actual auth check)
  const isAdmin = localStorage.getItem('dcTech_user') ? 
    JSON.parse(localStorage.getItem('dcTech_user'))?.userType === 'admin' : false;

  const tabs = [
    { id: 'contact', label: 'Contact for Quote', public: true, icon: 'envelope' },
    { id: 'compact', label: 'Compact FlatZero', public: false, icon: 'cog', badge: 'FUTURE' },
    { id: 'medium', label: 'Medium FlatZero', public: false, icon: 'cog', badge: 'FUTURE' },
    { id: 'large', label: 'Large FlatZero', public: false, icon: 'cog', badge: 'FUTURE' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await fetch(`${API_URL}/api/tire-quote-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          product: { name: 'General Inquiry', size: 'N/A', price: 'Quote Requested' }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit quote request');
      }

      setFormSubmitted(true);
      setFormData({
        name: '', email: '', phone: '', company: '',
        industry: '', fleetSize: '', vehicleTypes: '', description: '', timeline: ''
      });
    } catch (error) {
      console.error('Error submitting quote:', error);
      setSubmitError('Failed to submit your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Compact FlatZero Data (Shop-Level)
  const compactData = {
    title: "Compact FlatZero System",
    subtitle: "Shop-Level Solution",
    description: "Low entry, high profit, compact All-in-One for Premium Tires. Perfect for tire shops and low-volume production.",
    images: [
      "/images/flatzero/compact_page1_img1.png",
      "/images/flatzero/compact_page2_img1.png",
      "/images/flatzero/compact_page3_img1.png",
      "/images/flatzero/compact_page4_img1.png"
    ],
    dailyOutput: "40-50 passenger car self-sealing tires",
    floorSpace: "≈ 4 m²",
    voltage: "220 V with expansion ports",
    lifespan: "15-year life",
    packages: [
      {
        name: "Smart Self-Sealing Tire Package",
        items: [
          { model: "FZ2603-S40L", name: "Self-Sealing Tire Grinding & Sealant Spraying Integrated Machine", specs: "Compact 3-in-1 machine, 5.0 kW, 320kg" },
          { model: "FZ2596-CSG", name: "Advanced Polymer Self-Sealing Tire Sealant for Cars", specs: "1 ton included, -40°C to 140°C range" }
        ]
      }
    ],
    accessories: [
      { model: "SWW-125", name: "Steel Wire Wheel", description: "For roughening inner liner before adhesive spraying" },
      { model: "FZ2596-BJB", name: "Spray Glue Machine Repair Spare Parts Kit", description: "Minimizes unplanned downtime" },
      { model: "FZ2596-QXJ", name: "Tire Cleaning Bracket", description: "High-speed dust and debris removal" },
      { model: "FZ2596-RFJ", name: "High-power Hot Air Blower", description: "Pre-heats carcass to recommended temperature" }
    ],
    processingTimes: [
      { type: "10\" - 12\" motorcycle tires", time: "15s / pcs" },
      { type: "15\" - 18\" car tires", time: "45s / pcs" },
      { type: "19\" - 22\" car tires", time: "60s / pcs" },
      { type: "10R, 11R, 12R/22.5\" truck tires", time: "250s / pcs" }
    ]
  };

  // Medium FlatZero Data (Regional-Level)
  const mediumData = {
    title: "Medium FlatZero System",
    subtitle: "Regional-Level Solution",
    description: "Modular design with low-cost intelligent infinite scalability. Ideal for urban workshops and regional wholesalers.",
    images: [
      "/images/flatzero/medium_page1_img1.jpeg",
      "/images/flatzero/medium_page2_img1.jpeg",
      "/images/flatzero/medium_page3_img1.jpeg",
      "/images/flatzero/medium_page4_img1.jpeg",
      "/images/flatzero/medium_page5_img1.jpeg",
      "/images/flatzero/medium_page6_img1.jpeg",
      "/images/flatzero/medium_page7_img1.jpeg",
      "/images/flatzero/medium_page8_img1.jpeg",
      "/images/flatzero/medium_page9_img1.jpeg",
      "/images/flatzero/medium_page10_img1.jpeg",
      "/images/flatzero/medium_page11_img1.jpeg",
      "/images/flatzero/medium_page12_img1.jpeg"
    ],
    dailyOutput: "160-230 passenger car self-sealing tires",
    floorSpace: "≈ 12 m²",
    voltage: "380 V with expansion ports",
    lifespan: "15-year life",
    packages: [
      {
        name: "Package 1: 3-in-1 Smart Self-Sealing Tire Package",
        output: "50-60 tires/day",
        items: [
          { model: "FZ2596-B40", name: "Self-Sealing Tire Sealant Spraying Machine", specs: "7.0 kW, 388kg, Automatic Alignment" },
          { model: "FZ2596-DM01", name: "Tire Inner Liner Grinding Machine", specs: "2.9 kW, 185kg, 10-year lifespan" },
          { model: "FZ2596-CSG", name: "Advanced Polymer Self-Sealing Tire Sealant", specs: "1 ton included" }
        ]
      },
      {
        name: "Package 2: 4-in-1 Scale-Up Edition",
        output: "80-100 tires/day",
        items: [
          { model: "FZ2596-B40", name: "Self-Sealing Tire Sealant Spraying Machine", specs: "7.0 kW, 388kg" },
          { model: "FZ2596-DM01", name: "Tire Inner Liner Grinding Machine", specs: "2.9 kW, 185kg" },
          { model: "FZ2596-CSG", name: "Advanced Polymer Self-Sealing Tire Sealant", specs: "1 ton included" },
          { model: "FZ2596-Y21", name: "Cooling Roller Extension Unit", specs: "1.5 kW, 203kg" }
        ]
      },
      {
        name: "Package 3: 5-in-1 Mass-Production Edition",
        output: "160-230 tires/day",
        items: [
          { model: "FZ2596-B40", name: "Self-Sealing Tire Sealant Spraying Machine", specs: "7.0 kW" },
          { model: "FZ2596-DM01", name: "Tire Inner Liner Grinding Machine", specs: "2.9 kW" },
          { model: "FZ2596-CSG", name: "Advanced Polymer Self-Sealing Tire Sealant", specs: "2 tons included" },
          { model: "FZ2596-Y21", name: "Cooling Roller Extension Unit", specs: "1.5 kW" },
          { model: "FZ2596-J100", name: "100L Sealant Melting Extension Unit", specs: "9.0 kW, 280kg" }
        ]
      }
    ],
    accessories: [
      { model: "SWW-125", name: "Steel Wire Wheel", description: "For roughening inner liner" },
      { model: "FZ2596-BJB", name: "Spare Parts Kit", description: "Minimizes downtime" },
      { model: "FZ2596-QXJ", name: "Tire Cleaning Bracket", description: "One-operator debris removal" },
      { model: "FZ2596-RFJ", name: "Hot Air Blower", description: "Pre-heats carcass" }
    ],
    processingTimes: [
      { type: "10\" - 12\" motorcycle tires", time: "15s / pcs" },
      { type: "15\" - 18\" car tires", time: "45s / pcs" },
      { type: "19\" - 22\" car tires", time: "60s / pcs" },
      { type: "10R, 11R, 12R/22.5\" truck tires", time: "250s / pcs" }
    ]
  };

  // Large FlatZero Data (Production-Level)
  const largeData = {
    title: "Large FlatZero System",
    subtitle: "Production-Level Solution",
    description: "High-capacity modular system for large workshops, chain stores, and national distributors. Maximum throughput with infinite scalability.",
    images: [
      "/images/flatzero/large_page1_img1.jpeg",
      "/images/flatzero/large_page2_img1.jpeg",
      "/images/flatzero/large_page3_img1.jpeg",
      "/images/flatzero/large_page4_img1.jpeg",
      "/images/flatzero/large_page5_img1.jpeg",
      "/images/flatzero/large_page6_img1.jpeg",
      "/images/flatzero/large_page7_img1.jpeg",
      "/images/flatzero/large_page8_img1.jpeg",
      "/images/flatzero/large_page9_img1.jpeg",
      "/images/flatzero/large_page10_img1.jpeg",
      "/images/flatzero/large_page11_img1.jpeg",
      "/images/flatzero/large_page12_img1.jpeg",
      "/images/flatzero/large_page13_img1.jpeg"
    ],
    dailyOutput: "360 passenger car / 160 truck self-sealing tires",
    floorSpace: "≈ 20 m²",
    voltage: "380 V with expansion ports",
    lifespan: "15-year life",
    packages: [
      {
        name: "Package 1: 3-in-1 Smart Puncture-Proof Package",
        output: "180 car / 50 truck tires per day",
        items: [
          { model: "FZ-TRKM60L", name: "60L Large-Size Optimized Sealant Spraying Machine", specs: "13.0 kW, 580kg, Automatic Alignment" },
          { model: "FZ-TRKDM", name: "Large-Size Optimized Tire Inner Liner Grinding Machine", specs: "6.0 kW, 480.5kg" },
          { model: "FZ2512-TSG", name: "Advanced Polymer Self-Sealing Tire Sealant for Trucks", specs: "1 ton included" },
          { model: "FZ-TRKR3M", name: "3m Cooling Roller Extension Unit", specs: "3.0 kW, 446kg" },
          { model: "FZ-TRKT100L", name: "100L Dual-Layer Sealant Melting Extension", specs: "11.0 kW, 280kg" }
        ]
      },
      {
        name: "Package 2: 4-in-1 Scale-Up Edition",
        output: "240 car / 80 truck tires per day",
        items: [
          { model: "FZ-TRKM60L", name: "60L Large-Size Sealant Spraying Machine", specs: "13.0 kW" },
          { model: "FZ-TRKDM", name: "Large-Size Grinding Machine", specs: "6.0 kW" },
          { model: "FZ2512-TSG", name: "Truck Sealant", specs: "2 tons included" },
          { model: "FZ-TRKR3M", name: "3m Cooling Roller Extension", specs: "Qty: 2" },
          { model: "FZ-TRKT100L", name: "100L Melting Extension", specs: "11.0 kW" }
        ]
      },
      {
        name: "Package 3: 6-in-1 Mass-Production Edition",
        output: "360 car / 160 truck tires per day",
        items: [
          { model: "FZ-TRKM60L", name: "60L Large-Size Sealant Spraying Machine", specs: "13.0 kW" },
          { model: "FZ-TRKDM", name: "Large-Size Grinding Machine", specs: "6.0 kW" },
          { model: "FZ2512-TSG", name: "Truck Sealant", specs: "3 tons included" },
          { model: "FZ-TRKR3M", name: "3m Cooling Roller Extension", specs: "Qty: 3" },
          { model: "FZ-TRKT100L", name: "100L Melting Extension", specs: "Qty: 2" }
        ]
      }
    ],
    accessories: [
      { model: "SWW-125", name: "Steel Wire Wheel", description: "For roughening inner liner" },
      { model: "FZ2596-BJB", name: "Spare Parts Kit", description: "Minimizes downtime" },
      { model: "FZ2596-QXJ", name: "Tire Cleaning Bracket", description: "One-operator debris removal" },
      { model: "FZ2596-RFJ", name: "Hot Air Blower", description: "Pre-heats carcass" }
    ],
    processingTimes: [
      { type: "15\" - 18\" car tires", time: "45s / pcs" },
      { type: "19\" - 22\" car tires", time: "60s / pcs" },
      { type: "10R, 11R, 12R/22.5\" truck tires", time: "180s / pcs" }
    ]
  };

  const [selectedImage, setSelectedImage] = useState(0);

  const renderProductTab = (data) => (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">{data.title}</h2>
        <p className="text-red-400 text-lg mb-4">{data.subtitle}</p>
        <p className="text-gray-400 max-w-3xl mx-auto">{data.description}</p>
      </div>

      {/* Product Image Gallery */}
      {data.images && data.images.length > 0 && (
        <div className="space-y-4">
          {/* Main Image Display */}
          <div className="flex justify-center">
            <div className="relative max-w-4xl w-full">
              <div className="bg-gradient-to-b from-neutral-800 to-neutral-900 rounded-2xl p-4 border border-red-900/30 shadow-2xl">
                <img 
                  src={data.images[selectedImage] || data.images[0]} 
                  alt={`${data.title} - Image ${selectedImage + 1}`}
                  className="w-full h-auto rounded-lg object-contain max-h-[600px]"
                  data-testid={`product-main-image-${data.title.toLowerCase().replace(/\s+/g, '-')}`}
                />
                <div className="absolute top-6 right-6 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {data.subtitle}
                </div>
              </div>
            </div>
          </div>

          {/* Thumbnail Gallery */}
          <div className="flex justify-center">
            <div className="flex flex-wrap gap-2 justify-center max-w-4xl">
              {data.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  data-testid={`thumbnail-${idx}`}
                  className={`relative w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImage === idx 
                      ? 'border-red-500 ring-2 ring-red-500/50' 
                      : 'border-neutral-700 hover:border-red-400'
                  }`}
                >
                  <img 
                    src={img} 
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {selectedImage === idx && (
                    <div className="absolute inset-0 bg-red-500/20"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
          
          <p className="text-center text-gray-500 text-sm">
            Click thumbnails to view product brochure pages ({data.images.length} pages)
          </p>
        </div>
      )}

      {/* Key Specs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-neutral-800 rounded-lg p-4 border border-red-900/30 text-center">
          <div className="text-red-400 text-sm font-medium mb-1">Daily Output</div>
          <div className="text-white font-bold">{data.dailyOutput}</div>
        </div>
        <div className="bg-neutral-800 rounded-lg p-4 border border-red-900/30 text-center">
          <div className="text-red-400 text-sm font-medium mb-1">Floor Space</div>
          <div className="text-white font-bold">{data.floorSpace}</div>
        </div>
        <div className="bg-neutral-800 rounded-lg p-4 border border-red-900/30 text-center">
          <div className="text-red-400 text-sm font-medium mb-1">Voltage</div>
          <div className="text-white font-bold">{data.voltage}</div>
        </div>
        <div className="bg-neutral-800 rounded-lg p-4 border border-red-900/30 text-center">
          <div className="text-red-400 text-sm font-medium mb-1">Lifespan</div>
          <div className="text-white font-bold">{data.lifespan}</div>
        </div>
      </div>

      {/* Packages */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          Available Packages
        </h3>
        <div className="space-y-4">
          {data.packages.map((pkg, idx) => (
            <div key={idx} className="bg-neutral-800/50 rounded-lg border border-neutral-700 overflow-hidden">
              <div className="bg-red-900/30 px-4 py-3 flex justify-between items-center">
                <span className="font-semibold text-white">{pkg.name}</span>
                {pkg.output && <span className="text-red-300 text-sm">{pkg.output}</span>}
              </div>
              <div className="p-4">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-400 text-sm border-b border-neutral-700">
                      <th className="pb-2 w-1/4">Model</th>
                      <th className="pb-2">Product Name</th>
                      <th className="pb-2 w-1/3">Specifications</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pkg.items.map((item, itemIdx) => (
                      <tr key={itemIdx} className="border-b border-neutral-700/50 last:border-0">
                        <td className="py-3 text-red-400 font-mono text-sm">{item.model}</td>
                        <td className="py-3 text-white">{item.name}</td>
                        <td className="py-3 text-gray-400 text-sm">{item.specs}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Processing Times */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Processing Times
        </h3>
        <div className="bg-neutral-800/50 rounded-lg border border-neutral-700 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-800">
                <th className="px-4 py-3 text-left text-gray-400 text-sm">Tire Type</th>
                <th className="px-4 py-3 text-right text-gray-400 text-sm">Time per Piece</th>
              </tr>
            </thead>
            <tbody>
              {data.processingTimes.map((time, idx) => (
                <tr key={idx} className="border-t border-neutral-700">
                  <td className="px-4 py-3 text-white">{time.type}</td>
                  <td className="px-4 py-3 text-red-400 text-right font-mono">{time.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Accessories */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Ancillary Equipment / Accessories
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          {data.accessories.map((acc, idx) => (
            <div key={idx} className="bg-neutral-800/50 rounded-lg p-4 border border-neutral-700">
              <div className="flex justify-between items-start mb-2">
                <span className="text-white font-medium">{acc.name}</span>
                <span className="text-red-400 font-mono text-sm">{acc.model}</span>
              </div>
              <p className="text-gray-400 text-sm">{acc.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderContactTab = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Request a Quote</h2>
        <p className="text-gray-400">
          Interested in our FlatZero Puncture-Proof Tire Systems? Fill out the form below and our team will contact you within 24 hours.
        </p>
      </div>

      {formSubmitted ? (
        <div className="bg-neutral-800 rounded-2xl p-8 border border-red-900/30 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h4 className="text-xl font-bold text-white mb-2">Thank You!</h4>
          <p className="text-gray-400 mb-6">
            Your quote request has been submitted. Our team will contact you within 24 hours.
          </p>
          <button 
            onClick={() => setFormSubmitted(false)}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
          >
            Submit Another Request
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-neutral-800 rounded-2xl p-6 border border-red-900/30 space-y-6">
          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text" name="name" value={formData.name} onChange={handleInputChange} required
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email Address *</label>
                <input
                  type="email" name="email" value={formData.email} onChange={handleInputChange} required
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Phone Number *</label>
                <input
                  type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Company Name *</label>
                <input
                  type="text" name="company" value={formData.company} onChange={handleInputChange} required
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                  placeholder="ABC Industries"
                />
              </div>
            </div>
          </div>

          {/* Business Info */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Business Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Industry</label>
                <select
                  name="industry" value={formData.industry} onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-red-500"
                >
                  <option value="">Select Industry</option>
                  <option value="tire-shop">Tire Shop / Garage</option>
                  <option value="fleet-management">Fleet Management</option>
                  <option value="distributor">Tire Distributor</option>
                  <option value="manufacturer">Manufacturer</option>
                  <option value="construction">Construction</option>
                  <option value="logistics">Logistics & Transportation</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Expected Daily Volume</label>
                <select
                  name="fleetSize" value={formData.fleetSize} onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-red-500"
                >
                  <option value="">Select Volume</option>
                  <option value="1-50">1-50 tires/day</option>
                  <option value="50-100">50-100 tires/day</option>
                  <option value="100-200">100-200 tires/day</option>
                  <option value="200+">200+ tires/day</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tire Types of Interest</label>
                <input
                  type="text" name="vehicleTypes" value={formData.vehicleTypes} onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                  placeholder="e.g., Passenger cars, Trucks, Motorcycles"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Timeline</label>
                <select
                  name="timeline" value={formData.timeline} onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-red-500"
                >
                  <option value="">Select Timeline</option>
                  <option value="immediate">Immediate (within 2 weeks)</option>
                  <option value="1-month">Within 1 month</option>
                  <option value="1-3-months">1-3 months</option>
                  <option value="researching">Just researching</option>
                </select>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Describe What You&apos;re Looking For *
            </label>
            <textarea
              name="description" value={formData.description} onChange={handleInputChange} required rows={4}
              className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 resize-none"
              placeholder="Tell us about your business needs, current challenges, and what you're hoping to achieve with the FlatZero system..."
            />
          </div>

          {submitError && (
            <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm">
              {submitError}
            </div>
          )}

          <button
            type="submit" disabled={isSubmitting}
            className="w-full py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </>
            ) : 'Submit Quote Request'}
          </button>
        </form>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-neutral-900 to-black">
      {/* Header */}
      <div className="bg-red-900/30 backdrop-blur-sm border-b border-red-800/50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center text-gray-300 hover:text-white transition-colors" data-testid="back-to-home">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-white">FlatZero Puncture-Proof Systems</h1>
          <div className="w-24"></div>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-red-900/20 to-black py-12 border-b border-red-900/30">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">Self-Sealing Tire Technology</h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Transform standard tires into puncture-proof, leak-free, safe tires. Operating range: -40°C to 140°C with 15-year lifespan.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-neutral-800 bg-neutral-900/50">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => {
              // Only show admin tabs to admins
              if (!tab.public && !isAdmin) return null;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSelectedImage(0);
                  }}
                  data-testid={`tab-${tab.id}`}
                  className={`px-6 py-4 font-medium whitespace-nowrap transition-colors relative ${
                    activeTab === tab.id
                      ? 'text-red-400 border-b-2 border-red-500'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <span className="flex items-center">
                    {tab.label}
                    {tab.badge && (
                      <span className="ml-2 px-2 py-0.5 bg-yellow-600/80 text-yellow-100 text-xs rounded-full">
                        {tab.badge}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="container mx-auto px-4 py-12">
        {activeTab === 'contact' && renderContactTab()}
        {activeTab === 'compact' && isAdmin && renderProductTab(compactData)}
        {activeTab === 'medium' && isAdmin && renderProductTab(mediumData)}
        {activeTab === 'large' && isAdmin && renderProductTab(largeData)}
      </div>

      <Footer />
    </div>
  );
};

export default AntiPunctureTires;
